/**
 * Стиль (§13).
 *
 * Отвечает за оформление ГОТОВОГО КОНТЕНТА, а не за внешность пользователя.
 * Слева — настройки, справа — живое превью теми же значениями, что уйдут
 * в генераторы графики (§27).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BrandKitInput, BrandKitView } from '@alterium/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { StylePreview } from '../components/brand/StylePreview';
import { brandApi } from '../api/looks';
import { useTranslation } from 'react-i18next';
import { errorText } from '../i18n/errors';

const colorFields = [
  { key: 'background', labelKey: 'style.color.background' },
  { key: 'primary', labelKey: 'style.color.primary' },
  { key: 'accent', labelKey: 'style.color.accent' },
  { key: 'text', labelKey: 'style.color.text' },
  { key: 'secondary', labelKey: 'style.color.secondary' },
] as const;

export function StylePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const kit = useQuery({ queryKey: ['brand-kit'], queryFn: brandApi.get });

  /**
   * Черновик правок: превью обновляется сразу, сохранение — по кнопке.
   * Не зеркалим серверные данные в эффекте — берём их как основу, пока
   * пользователь ничего не менял (React: you-might-not-need-an-effect).
   */
  const [edits, setEdits] = useState<BrandKitView | null>(null);
  const draft = edits ?? kit.data ?? null;

  const save = useMutation({
    mutationFn: (patch: Partial<BrandKitInput>) => brandApi.update(patch),
    onSuccess: async (updated) => {
      setEdits(updated);
      await queryClient.invalidateQueries({ queryKey: ['brand-kit'] });
    },
  });

  if (!draft) {
    return (
      <div>
        <PageHeader title={t('nav.style')} />
      </div>
    );
  }

  const patch = (changes: Partial<BrandKitView>) => setEdits({ ...draft, ...changes });

  return (
    <div>
      <PageHeader
        title={t('nav.style')}
        description={t('style.subtitle')}
        action={
          <Button
            variant="primary"
            onClick={() =>
              save.mutate({
                name: draft.name,
                colors: draft.colors,
                captions: draft.captions,
                cta: draft.cta,
                lowerThird: draft.lowerThird,
                logo: draft.logo,
                motion: draft.motion,
                music: draft.music,
              })
            }
            disabled={save.isPending}
          >
            {save.isPending ? t('common.saving') : t('common.save')}
          </Button>
        }
      />

      {save.isError ? (
        <div className="mb-6">
          <Alert tone="error">{errorText(save.error)}</Alert>
        </div>
      ) : null}
      {save.isSuccess ? (
        <div className="mb-6">
          <Alert tone="success">{t('style.saved')}</Alert>
        </div>
      ) : null}

      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <div className="flex flex-1 flex-col gap-6">
          <Card>
            <CardBody className="flex flex-col gap-4">
              <h2 className="text-card-title">{t('style.colors')}</h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {colorFields.map((field) => (
                  <label key={field.key} className="flex flex-col gap-1.5">
                    <span className="text-meta text-content-secondary">{t(field.labelKey)}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={draft.colors[field.key]}
                        onChange={(event) =>
                          patch({ colors: { ...draft.colors, [field.key]: event.target.value } })
                        }
                        className="h-8 w-10 cursor-pointer rounded border border-line bg-transparent"
                      />
                      <span className="text-meta tabular-nums text-content-muted">
                        {draft.colors[field.key]}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex flex-col gap-4">
              <h2 className="text-card-title">{t('style.captions')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label={t('style.position')}
                  value={draft.captions.position}
                  options={[
                    ['bottom', t('style.pos.bottom')],
                    ['center', t('style.pos.center')],
                    ['top', t('style.pos.top')],
                  ]}
                  onChange={(value) =>
                    patch({ captions: { ...draft.captions, position: value as 'bottom' } })
                  }
                />
                <Select
                  label={t('style.animation')}
                  value={draft.captions.animation}
                  options={[
                    ['karaoke', t('style.anim.karaoke')],
                    ['fade', t('style.anim.fade')],
                    ['pop', t('style.anim.pop')],
                    ['none', t('style.anim.none')],
                  ]}
                  onChange={(value) =>
                    patch({ captions: { ...draft.captions, animation: value as 'karaoke' } })
                  }
                />
                <Select
                  label={t('style.background')}
                  value={draft.captions.background}
                  options={[
                    ['shadow', t('style.bg.shadow')],
                    ['solid', t('style.bg.solid')],
                    ['none', t('style.bg.none')],
                  ]}
                  onChange={(value) =>
                    patch({ captions: { ...draft.captions, background: value as 'shadow' } })
                  }
                />
                <label className="flex flex-col gap-1.5">
                  <span className="text-meta text-content-secondary">{t('style.anim.karaoke')}</span>
                  <input
                    type="color"
                    value={draft.captions.activeWordColor}
                    onChange={(event) =>
                      patch({
                        captions: { ...draft.captions, activeWordColor: event.target.value },
                      })
                    }
                    className="h-10 w-full cursor-pointer rounded-control border border-line bg-transparent"
                  />
                </label>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex flex-col gap-4">
              <h2 className="text-card-title">Lower third</h2>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('style.lowerThird.name')}
                  value={draft.lowerThird.name}
                  onChange={(event) =>
                    patch({ lowerThird: { ...draft.lowerThird, name: event.target.value } })
                  }
                />
                <Input
                  label={t('style.lowerThird.role')}
                  value={draft.lowerThird.title}
                  onChange={(event) =>
                    patch({ lowerThird: { ...draft.lowerThird, title: event.target.value } })
                  }
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex flex-col gap-4">
              <h2 className="text-card-title">{t('style.voice')}</h2>

              <label className="flex flex-col gap-1.5">
                <span className="text-meta text-content-secondary">
                  {t('style.voiceSpeed', { value: draft.voice.speed.toFixed(2) })}
                </span>
                <input
                  type="range"
                  min={70}
                  max={130}
                  value={Math.round(draft.voice.speed * 100)}
                  onChange={(event) =>
                    patch({ voice: { ...draft.voice, speed: Number(event.target.value) / 100 } })
                  }
                  className="h-10 accent-accent"
                />
              </label>

              {/*
                Границы уже сужены: провайдер принимает 0.5-2.0, но за
                пределами 0.7-1.3 голос перестаёт быть похож на человека.
              */}
              <p className="text-meta text-content-muted">{t('style.voiceHint')}</p>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex flex-col gap-4">
              <h2 className="text-card-title">{t('style.music')}</h2>

              <label className="flex items-center gap-2 text-body text-content">
                <input
                  type="checkbox"
                  checked={draft.music.enabled}
                  onChange={(event) =>
                    patch({ music: { ...draft.music, enabled: event.target.checked } })
                  }
                  className="h-4 w-4 accent-accent"
                />
                {t('style.musicEnabled')}
              </label>

              {draft.music.enabled ? (
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label={t('style.musicMood')}
                    value={draft.music.mood}
                    onChange={(event) =>
                      patch({ music: { ...draft.music, mood: event.target.value } })
                    }
                  />
                  <label className="flex flex-col gap-1.5">
                    <span className="text-meta text-content-secondary">
                      {t('style.musicVolume', { value: Math.round(draft.music.volume * 100) })}
                    </span>
                    <input
                      type="range"
                      min={0}
                      max={60}
                      value={Math.round(draft.music.volume * 100)}
                      onChange={(event) =>
                        patch({ music: { ...draft.music, volume: Number(event.target.value) / 100 } })
                      }
                      className="h-10 accent-accent"
                    />
                  </label>
                </div>
              ) : null}

              {/* Музыка громче речи — главная причина, по которой её выключают. */}
              <p className="text-meta text-content-muted">{t('style.musicHint')}</p>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex flex-col gap-4">
              <h2 className="text-card-title">CTA</h2>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('style.cta.text')}
                  value={draft.cta.text}
                  onChange={(event) => patch({ cta: { ...draft.cta, text: event.target.value } })}
                />
                <Select
                  label={t('style.cta.style')}
                  value={draft.cta.buttonStyle}
                  options={[
                    ['solid', t('style.cta.solid')],
                    ['outline', t('style.cta.outline')],
                    ['text', t('style.cta.plain')],
                  ]}
                  onChange={(value) =>
                    patch({ cta: { ...draft.cta, buttonStyle: value as 'solid' } })
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="lg:sticky lg:top-10">
          <p className="mb-3 text-meta text-content-secondary">{t('style.preview')}</p>
          <StylePreview kit={draft} />
        </div>
      </div>
    </div>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-meta text-content-secondary">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 rounded-control border border-line bg-surface-secondary px-3 text-body text-content transition-colors duration-fast hover:border-line-strong focus:border-accent focus:outline-none"
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}
